import ImageComponent from '@/components/ImageComponent';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { ImageProps } from 'next/image';
import placeholder from '@/assets/images/placeholder.png';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import { getLocation } from '@/utils/getVenueLocation';
import RequestQuoteButton from '@/components/RequestQuoteButton';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import type { MeasurementUnit } from '@cvent/venue-search-shared/types';
import { type ReactNode, useMemo } from 'react';
import RatingReviewDistance from '@/components/card/RatingReviewDistance';
import type { FullMapListViewStrings } from '@/components/maps/list-view/FullMapListView';
import { Shimmer, toBase64 } from '@/components/skeletons/ImageSkeleton';
import type { Criteria } from '@/fetchers/getFastResponderCriteria';
import ResponderTag from '@/components/card/tags/ResponderTag';
import { TOP_RESPONDER_TAG_VARIANT } from '@/constants/filterConstants';

type FullMapVenueCardProps = {
  children: ReactNode;
  venue: CsnSearchVenue;
  venueIndex: number;
  locale: Locale;
  isLoggedIn: boolean;
  hideVenueStats: boolean;
  activeFilters: string;
  isRFPFullAccess: boolean;
  fullMapListViewStrings: FullMapListViewStrings;
  shoppingCartItem?: ShoppingCartItem;
  measurementUnit: MeasurementUnit;
  canShowDistanceOnCards: boolean;
  isListing: boolean;
  fastResponderCriteria: Criteria;
  isFastResponderEnabled: number;
};

export default function FullMapVenueCard({
  children,
  venue,
  venueIndex,
  locale,
  isLoggedIn,
  hideVenueStats,
  activeFilters,
  isRFPFullAccess,
  fullMapListViewStrings,
  shoppingCartItem,
  measurementUnit,
  canShowDistanceOnCards,
  isListing,
  fastResponderCriteria,
  isFastResponderEnabled
}: FullMapVenueCardProps): JSX.Element {
  const {
    ratingAndReviewStrings,
    requestQuoteButtonStrings,
    venueImageAltText,
    screenReaderText,
    fastResponderStrings
  } = fullMapListViewStrings;

  const venueLocation = useMemo(
    () => getLocation(venue.city, venue.stateProvinceCode, venue.countryCode),
    [venue.city, venue.countryCode, venue.stateProvinceCode]
  );

  let imgAltText = venue.name;
  if (venueLocation) {
    imgAltText = formatMessage(venueImageAltText, locale, {
      venueName: venue.name,
      venueLocation
    });
  }
  const venueImageSrc = useMemo(
    () =>
      venue.mainImage?.large ??
      venue.mainImage?.original ??
      venue.mainImage?.medium ??
      venue.mainImage?.small ??
      venue.mainImage?.extraSmall ??
      venue.firstProfileImageUrl,
    [
      venue.firstProfileImageUrl,
      venue.mainImage?.extraSmall,
      venue.mainImage?.large,
      venue.mainImage?.medium,
      venue.mainImage?.original,
      venue.mainImage?.small
    ]
  );
  const imageProps: ImageProps = {
    fill: true,
    sizes: '106px',
    style: { objectFit: 'cover' },
    src: venue.displaySearchResultImage && venueImageSrc ? venueImageSrc : placeholder.src,
    alt: imgAltText,
    placeholder: `data:image/svg+xml;base64,${toBase64(Shimmer(106, 80))}`
  };

  return (
    <div
      className={`${
        isListing ? 'bg-hb-background' : 'bg-background'
      } my-4 flex h-[152px] w-full flex-col justify-between gap-1 rounded-lg shadow-2`}
      data-cvent-id={`list-view-venue-card-full-map-${venue.id}`}
    >
      <div className="flex min-h-[80px] w-full gap-2">
        {/* show promoted text on top left of image */}
        {isListing && (
          <div className="absolute z-10 rounded-br-md rounded-tl-md bg-warning-10 px-2 py-0.5 text-b-2xs font-bold">
            {formatMessage(fullMapListViewStrings.promoted, locale)}
          </div>
        )}
        <div className="relative w-[106px] flex-shrink-0 overflow-hidden rounded-br-md rounded-tl-md">
          <ImageComponent imageProps={imageProps} />
        </div>
        <div className="flex h-full w-full flex-col overflow-hidden pr-2 pt-2">
          <div className="flex justify-between">
            <div className="line-clamp-2 w-full pr-2">
              {isFastResponderEnabled >= TOP_RESPONDER_TAG_VARIANT && venue.fastResponder && (
                <ResponderTag
                  strings={fastResponderStrings}
                  locale={locale}
                  fastResponderCriteria={fastResponderCriteria}
                  isFullMapVenueCard
                />
              )}
              <h3
                className={`break-words text-b-base/normal ${isListing ? 'text-hb-brand-60' : 'text-brand-60'}`}
                title={venue.name}
              >
                {venue.name}
              </h3>
            </div>
            {shoppingCartItem && (
              <div>
                <RequestQuoteButton
                  activeFilters={activeFilters}
                  strings={requestQuoteButtonStrings}
                  item={shoppingCartItem}
                  iconOnly
                  className="!h-6 !min-h-0 !w-6 !rounded-sm"
                  isRFPFullAccess={isRFPFullAccess}
                  displayTypes={venue.displayTypes}
                  isLoggedIn={isLoggedIn}
                  testID={`full-map-venue-container-${venueIndex + 1}`}
                />
              </div>
            )}
          </div>
          {!hideVenueStats && (
            <RatingReviewDistance
              ratings={venue.preferredRating}
              reviews={venue.plannerReviewsRating}
              screenReaderText={screenReaderText}
              measurementUnit={measurementUnit}
              showDistance={
                canShowDistanceOnCards && venue.type.toString() !== 'CVB' && venue.type.toString() !== 'DMC'
              }
              distanceInMetric={venue.distanceFromSearchLocation?.metricValue}
              distanceInImperial={venue.distanceFromSearchLocation?.imperialValue}
              locale={locale}
              strings={ratingAndReviewStrings}
              isListViewInFullMap
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
